import React, { useState, useEffect } from 'react';
import { Loader2, Database, Shield, Users, BarChart } from 'lucide-react';

interface LoadingScreenProps {
  message?: string;
  type?: 'initial' | 'auth' | 'data' | 'query' | 'connection';
  progress?: number;
  timeout?: number;
  onTimeout?: () => void;
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message,
  type = 'initial',
  progress,
  timeout = 30000, // 30 seconds default timeout
  onTimeout
}) => {
  const [dots, setDots] = useState('');
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setTimeoutReached(true);
      setShowRetry(true);
      onTimeout?.();
    }, timeout);

    return () => clearTimeout(timeoutId);
  }, [timeout, onTimeout]);

  const getLoadingContent = () => {
    switch (type) {
      case 'auth':
        return {
          icon: <Shield className="w-12 h-12 text-blue-400" />,
          title: 'Authenticating',
          description: 'Verifying your credentials and setting up your session',
          tips: [
            'Checking your authentication status',
            'Loading your user preferences',
            'Setting up secure session'
          ]
        };
      case 'data':
        return {
          icon: <Database className="w-12 h-12 text-green-400" />,
          title: 'Loading Data',
          description: 'Connecting to your data sources and loading schemas',
          tips: [
            'Establishing database connections',
            'Loading table schemas',
            'Preparing query interface'
          ]
        };
      case 'query':
        return {
          icon: <BarChart className="w-12 h-12 text-purple-400" />,
          title: 'Processing Query',
          description: 'Executing your query and analyzing results',
          tips: [
            'Optimizing query performance',
            'Executing database operations',
            'Preparing result visualization'
          ]
        };
      case 'connection':
        return {
          icon: <Database className="w-12 h-12 text-orange-400" />,
          title: 'Connecting',
          description: 'Establishing connection to your database',
          tips: [
            'Validating connection parameters',
            'Testing network connectivity',
            'Initializing database session'
          ]
        };
      default:
        return {
          icon: <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />,
          title: 'Loading QueryVerse',
          description: 'Initializing the application and loading your workspace',
          tips: [
            'Loading application components',
            'Initializing services',
            'Preparing your workspace'
          ]
        };
    }
  };

  const content = getLoadingContent();

  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          {content.icon}
        </div>
        
        <h1 className="text-2xl font-bold mb-2">
          {content.title}
          {!timeoutReached && dots}
        </h1>
        
        <p className="text-gray-400 mb-8">
          {content.description}
        </p>

        {progress !== undefined && (
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="space-y-3 mb-8">
          {content.tips.map((tip, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 text-sm text-gray-500"
            >
              <div className="w-2 h-2 bg-gray-600 rounded-full" />
              {tip}
            </div>
          ))}
        </div>

        {timeoutReached && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-6">
            <h3 className="text-red-400 font-semibold mb-2">Loading Timeout</h3>
            <p className="text-sm text-gray-400 mb-4">
              The application is taking longer than expected to load. This might be due to network issues or server load.
            </p>
            {showRetry && (
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={handleGoHome}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                >
                  Go Home
                </button>
              </div>
            )}
          </div>
        )}

        {message && (
          <div className="text-sm text-gray-500">
            {message}
          </div>
        )}

        {/* Network status indicator */}
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <div className={`w-2 h-2 rounded-full ${navigator.onLine ? 'bg-green-500' : 'bg-red-500'}`} />
            {navigator.onLine ? 'Connected' : 'Offline'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;